import { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { AdminNav } from "@/components/admin/AdminNav";
import { api } from "@/lib/api";
import { getCitySlug } from "@/lib/city";
import "@/styles/brand-a.css";
import "@/styles/admin-invites.css";

/**
 * Admin profile traffic — super_admin / pulseboard_admin only.
 * Route: /admin/traffic (API: GET /:citySlug/v1/admin/profile-traffic)
 *
 * First-party, aggregate counts per specialist profile: the evidence used
 * when pitching claims ("your page had N visits, M straight from Google").
 */

interface TrafficRow {
  facilityId: string;
  slug: string;
  name: string;
  specialty: string | null;
  views: number;
  searchViews: number;
  searchLandings: number;
  publicViews: number;
  clinicianViews: number;
  phoneClicks: number;
  websiteClicks: number;
  emailClicks: number;
  referralActions: number;
}

interface TrafficReport {
  days: number;
  from: string;
  to: string;
  items: TrafficRow[];
}

const WINDOWS = [7, 30, 90] as const;
const COLUMNS: Array<{ key: keyof TrafficRow; label: string; title: string }> = [
  { key: "views", label: "Views", title: "Profile views (one per browser session)" },
  { key: "searchLandings", label: "From search", title: "Landed on this profile straight from a search engine result" },
  { key: "searchViews", label: "Search sessions", title: "Views in sessions that started from a search engine" },
  { key: "publicViews", label: "Public", title: "Views by the public (patients, carers)" },
  { key: "clinicianViews", label: "Clinician", title: "Views by signed-in clinicians or GP mode" },
  { key: "phoneClicks", label: "Calls", title: "Phone number taps" },
  { key: "websiteClicks", label: "Website", title: "Practice website clicks" },
  { key: "referralActions", label: "Referral", title: "Referral details copied or letter drafted" },
];

export default function AdminProfileTraffic() {
  const citySlug = getCitySlug();
  const [days, setDays] = useState<(typeof WINDOWS)[number]>(30);
  const { data, isLoading, isError } = useQuery({
    queryKey: ["admin-profile-traffic", days],
    queryFn: () => api.get<TrafficReport>(`/${citySlug}/v1/admin/profile-traffic?days=${days}`),
    retry: false,
  });

  const totals = (data?.items ?? []).reduce<Record<string, number>>((sum, row) => {
    for (const column of COLUMNS) sum[column.key] = (sum[column.key] ?? 0) + Number(row[column.key]);
    return sum;
  }, {});

  return (
    <div className="brand-a" style={{ minHeight: "100dvh", padding: "80px 40px 60px" }}>
      <div style={{ maxWidth: 1100, margin: "0 auto" }}>
        <AdminNav />
        <div className="ba-eyebrow" style={{ marginBottom: 16 }}>Admin · Profile traffic</div>
        <h1 className="ba-display" style={{ fontSize: 40, marginBottom: 12 }}>Profile traffic</h1>
        <p style={{ fontSize: 14, color: "var(--ba-stone-7)", marginBottom: 24, maxWidth: 720 }}>
          First-party counts from PulseBoard itself. Views are counted once per browser session, bots
          are excluded, and nothing identifying is stored. “From search” counts visitors who arrived
          on the profile directly from a Google/Bing result.
          {data ? ` Window: ${data.from} → ${data.to} (Canberra time).` : null}
        </p>

        <div style={{ display: "flex", gap: 8, marginBottom: 20 }} role="group" aria-label="Time window">
          {WINDOWS.map((value) => (
            <button
              key={value}
              type="button"
              className="ba-btn ba-btn--sm"
              aria-pressed={days === value}
              style={{ opacity: days === value ? 1 : 0.55 }}
              onClick={() => setDays(value)}
            >
              Last {value} days
            </button>
          ))}
        </div>

        {isLoading ? <p className="ba-mono ba-mono--dim" style={{ fontSize: 13 }}>Loading…</p> : null}
        {isError ? (
          <p role="alert" style={{ color: "var(--ba-coral)", fontSize: 14 }}>
            Failed to load profile traffic. Check you are signed in with an admin role.
          </p>
        ) : null}

        {data ? (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
              <thead>
                <tr style={{ textAlign: "left", borderBottom: "1px solid rgba(15,42,42,0.15)" }}>
                  <th style={{ padding: "8px 6px" }}>Specialist</th>
                  {COLUMNS.map((column) => (
                    <th key={column.key} title={column.title} style={{ padding: "8px 6px", textAlign: "right" }}>
                      {column.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                <tr style={{ fontWeight: 600, borderBottom: "1px solid rgba(15,42,42,0.15)" }}>
                  <td style={{ padding: "8px 6px" }}>All {data.items.length} profiles</td>
                  {COLUMNS.map((column) => (
                    <td key={column.key} style={{ padding: "8px 6px", textAlign: "right" }}>
                      {totals[column.key] ?? 0}
                    </td>
                  ))}
                </tr>
                {data.items.map((row) => (
                  <tr key={row.facilityId} style={{ borderBottom: "1px solid rgba(15,42,42,0.06)" }}>
                    <td style={{ padding: "8px 6px" }}>
                      <Link to={`/directory/specialist/${row.slug}`}>{row.name}</Link>
                      {row.specialty ? (
                        <span style={{ display: "block", fontSize: 12, color: "var(--ba-stone-7)" }}>{row.specialty}</span>
                      ) : null}
                    </td>
                    {COLUMNS.map((column) => (
                      <td key={column.key} style={{ padding: "8px 6px", textAlign: "right" }}>
                        {row[column.key]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>
    </div>
  );
}
